import React, { useEffect, useState } from 'react';
import './HeroSection.css';

interface HeroSectionProps {
  onGetStarted?: () => void;
  onLearnMore?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onGetStarted, onLearnMore }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      title: "AI-Powered Optimization",
      image: "/Gemini_Generated_Image_7t3cth7t3cth7t3c_optimized.jpg"
    },
    {
      title: "Real-Time Analytics",
      image: "/Gemini_Generated_Image_qg83n1qg83n1qg83_optimized.jpg"
    },
    {
      title: "Scalable Architecture", 
      image: "/Gemini_Generated_Image_st1k7st1k7st1k7s_optimized.jpg"
    },
    {
      title: "Intelligent Forecasting",
      image: "/Gemini_Generated_Image_y6tdb1y6tdb1y6td_optimized.jpg"
    }
  ];

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Rotate through features
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  const handleGetDemo = () => {
    // Scroll to "Get Started Today" section
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className={`hero-section ${isVisible ? 'visible' : ''}`} aria-labelledby="hero-heading">
      <div className="hero-container">
        <div className="hero-content">
          <div className="hero-text">
            <h1 id="hero-heading" className="hero-title">
              Transform Your Supply Chain{' '}
              <span className="hero-highlight">
                {features[currentFeature].title}
              </span>
            </h1>
            <p className="hero-subtitle">
              Optinode AI revolutionizes warehouse optimization with advanced algorithms, 
              real-time analytics, and intelligent decision-making to maximize efficiency 
              and minimize costs across your entire distribution network.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">40%</span>
                <span className="stat-label">Cost Reduction</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">60%</span>
                <span className="stat-label">Faster Planning</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">99.9%</span>
                <span className="stat-label">Uptime</span>
              </div>
            </div>
            <div className="hero-actions">
              <button 
                className="btn btn-primary btn-large"
                onClick={onGetStarted}
                aria-label="Get started with Optinode AI"
              >
                Get Started Free
              </button>
              <button 
                className="btn btn-secondary btn-large"
                onClick={handleGetDemo}
                aria-label="Get a demo of our platform"
              >
                Get A Demo
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="revolving-images-container">
              <div className="revolving-images">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className={`revolving-image ${currentFeature === index ? 'active' : ''}`}
                  >
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="feature-image"
                    />
                    <div className="image-overlay">
                      <h3>{feature.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
              <div className="image-indicators">
                {features.map((_, index) => (
                  <div 
                    key={index}
                    className={`indicator ${currentFeature === index ? 'active' : ''}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;